import { FormAutocomplete, FormDecimal, FormInteger, FormText } from "@itk/mui-components";
import { FormFieldController, FormFieldState, GridFormRow } from "@itk/react-form";
import { createFilterOptions, Typography } from "@mui/material";
import { useMemo } from "react";
import { Model_AttributeDetailsGet, Shared_AttributeTypes } from "src/backend/openapi/v1";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";

type AttributeOption = { description: string; value: string };
const filterOptions = createFilterOptions<AttributeOption>({ stringify: o => o.description });

type Props = {
  formItem: GridFormRow<Model_AttributeDetailsGet>;
  handleChange?: (attributeId: string | null, value: string | null) => void;
};

export const AttributeRenderer = (props: Props) => {
  const { formItem, handleChange } = props;

  const genericTranslationsT = useGenericTranslationsT();

  const valueDoesntExistInOptions = formItem.formState.options.value?.find(option => option.value === formItem.formState.value.value) === undefined;

  const optionsWithExtraValue = useMemo(() => {
    const result = [...(formItem.formState.options.value || [])];
    if (valueDoesntExistInOptions && formItem.formState.value.value) {
      result.push({ description: formItem.formState.valueDescription.value || "", value: formItem.formState.value.value || "" });
    }
    return result;
  }, [formItem.formState.options.value, formItem.formState.valueDescription.value, formItem.formState.value.value]);

  const getSortedOptions = (options: Model_AttributeDetailsGet["options"]) => {
    return options.map(({ description, value }) => ({ description, value })).sort((a, b) => a.description.localeCompare(b.description)) || [];
  };

  let renderItem = <></>;
  switch (formItem.formState.attributeType.value) {
    case Shared_AttributeTypes.Text:
      renderItem = (
        <FormText
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="dynamicValue"
          size="small"
          disabled={!!formItem.formState.disabled.value}
          tArgs={[formItem.formState.description.value || ""]}
          formFieldState={formItem.formState.value as FormFieldState<string>}
          formFieldController={formItem.formController.value}
          width="100%"
          sx={{ textTransform: "none" }}
          onBlurCapture={() => {
            handleChange && handleChange(formItem.formState.attributeId.value, formItem.formState.value.value);
          }}
        />
      );
      break;

    case Shared_AttributeTypes.Integer:
      renderItem = (
        <FormInteger
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="dynamicValue"
          size="small"
          disabled={!!formItem.formState.disabled.value}
          tArgs={[formItem.formState.description.value || ""]}
          formFieldState={formItem.formState.value as unknown as FormFieldState<number>}
          formFieldController={formItem.formController.value as unknown as FormFieldController<number>}
          width="100%"
          sx={{ textTransform: "none" }}
          onBlurCapture={() => {
            handleChange && handleChange(formItem.formState.attributeId.value, formItem.formState.value.value);
          }}
        />
      );
      break;

    case Shared_AttributeTypes.Decimal:
      renderItem = (
        <FormDecimal
          decimals={2}
          trailingZeroes={2}
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="dynamicValue"
          size="small"
          disabled={!!formItem.formState.disabled.value}
          tArgs={[formItem.formState.description.value || ""]}
          formFieldState={formItem.formState.value as unknown as FormFieldState<number>}
          formFieldController={formItem.formController.value as unknown as FormFieldController<number>}
          width="100%"
          sx={{ textTransform: "none" }}
          onBlurCapture={() => {
            handleChange && handleChange(formItem.formState.attributeId.value, formItem.formState.value.value);
          }}
        />
      );
      break;

    // TODO: add ui for boolean
    case Shared_AttributeTypes.Bool:
      renderItem = (
        <>
          <Typography>Boolean</Typography>
        </>
      );
      break;

    case Shared_AttributeTypes.Reference:
    case Shared_AttributeTypes.List:
      renderItem = (
        <FormAutocomplete
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="dynamicValue"
          size="small"
          disabled={!!formItem.formState.disabled.value}
          tArgs={[formItem.formState.description.value || ""]}
          formFieldState={formItem.formState.value}
          formFieldController={formItem.formController.value}
          options={getSortedOptions(optionsWithExtraValue)}
          width="100%"
          getId={option => option.value}
          getOptionLabel={option => option.description}
          sx={{ textTransform: "none" }}
          filterOptions={(options, state) => {
            const validValues = new Set((formItem.formState.options.value || []).map(o => o.value));
            return filterOptions(
              options.filter(o => validValues.has(o.value)),
              state
            );
          }}
          onChange={(_, value) => {
            handleChange && handleChange(formItem.formState.attributeId.value, value?.value ?? "");
          }}
        />
      );
      break;

    // TODO add color attribute
    // Commit id for color UI code
    // 025569cceea63dd1f3e2f1b8e4b1ef62d4116222
  }

  return renderItem;
};
